import { Formik, Form as FormikForm, Field, ErrorMessage } from 'formik';
import useLicenseTypeManagement from './hooks/useLicenseTypeManagement';
import { useNavigate } from 'react-router';
import * as Yup from 'yup';
import { toast } from 'sonner';
import { Button } from '@/src/shadComponents/ui/button';
import { Input } from '@/src/shadComponents/ui/input';
import { Textarea } from '@/src/shadComponents/ui/textarea';
import { Label } from '@/src/shadComponents/ui/label';
import { Card, CardContent } from '@/src/shadComponents/ui/card';
import { Loader2 } from 'lucide-react';
import PageShell from '@/src/Common/components/PageShell';

interface CreateLicenseTypeFormValues {
    name: string;
    description: string;
    adminName: string;
    simultaneousDeviceCount: number;
}

const validationSchema = Yup.object({
    name: Yup.string()
        .required('Name is required')
        .min(3, 'Name must be at least 3 characters')
        .max(50, 'Name must be less than 50 characters'),
    description: Yup.string()
        .required('Description is required')
        .max(500, 'Description must be less than 500 characters'),
    adminName: Yup.string()
        .required('Admin name is required')
        .min(3, 'Admin name must be at least 3 characters')
        .max(50, 'Admin name must be less than 50 characters'),
    simultaneousDeviceCount: Yup.number()
        .required('Device limit is required')
        .min(1, 'Device limit must be at least 1')
        .max(1000, 'Device limit must be less than 1000'),
});

export default function CreateLicenseType() {
    const initialValues: CreateLicenseTypeFormValues = {
        name: '',
        description: '',
        adminName: '',
        simultaneousDeviceCount: 1,
    };

    const navigate = useNavigate();
    const { createLicenseType } = useLicenseTypeManagement();

    return (
        <PageShell
            title="Create License Type"
            breadcrumbs={[
                { label: 'Admin', href: '/admin/licenses' },
                { label: 'License Types', href: '/admin/licenses' },
                { label: 'Create' },
            ]}
        >
            <Formik
                initialValues={initialValues}
                validationSchema={validationSchema}
                onSubmit={async (values, { setSubmitting, setFieldError }) => {
                    try {
                        await createLicenseType(values);
                        toast.success('License type created successfully');
                        navigate(-1);
                    } catch (error) {
                        console.error('Failed to create license type:', error);
                        if (error instanceof Error) {
                            toast.error(
                                error.message ||
                                    'Failed to create license type',
                            );
                        }
                    } finally {
                        setSubmitting(false);
                    }
                }}
            >
                {({ isSubmitting, errors, touched }) => (
                    <FormikForm
                        className="mt-6"
                        placeholder={undefined}
                        onPointerEnterCapture={undefined}
                        onPointerLeaveCapture={undefined}
                    >
                        <div className="space-y-12">
                            <div className="grid grid-cols-1 gap-x-8 gap-y-10 pb-12 md:grid-cols-3">
                                <div className="px-8">
                                    <h2 className="text-lg font-semibold tracking-tight text-foreground">
                                        License Type Details
                                    </h2>
                                    <p className="mt-1 text-sm leading-6 text-muted-foreground">
                                        Define the properties of this license
                                        type.
                                    </p>
                                </div>
                                <Card className="md:col-span-2">
                                    <CardContent className="pt-6 space-y-6">
                                        <div className="space-y-2">
                                            <Label htmlFor="name">Name</Label>
                                            <Field
                                                as={Input}
                                                id="name"
                                                name="name"
                                                placeholder="Enterprise"
                                            />
                                            <ErrorMessage
                                                name="name"
                                                component="div"
                                                className="text-sm text-destructive"
                                            />
                                        </div>

                                        <div className="space-y-2">
                                            <Label htmlFor="description">Description</Label>
                                            <Field
                                                as={Textarea}
                                                id="description"
                                                name="description"
                                                rows={3}
                                                placeholder="Enterprise license with all features enabled"
                                            />
                                            <ErrorMessage
                                                name="description"
                                                component="div"
                                                className="text-sm text-destructive"
                                            />
                                        </div>

                                        <div className="space-y-2">
                                            <Label htmlFor="adminName">Admin Name</Label>
                                            <Field
                                                as={Input}
                                                id="adminName"
                                                name="adminName"
                                                placeholder="Enterprise Admin"
                                            />
                                            <ErrorMessage
                                                name="adminName"
                                                component="div"
                                                className="text-sm text-destructive"
                                            />
                                        </div>

                                        <div className="space-y-2">
                                            <Label htmlFor="simultaneousDeviceCount">Device Limit</Label>
                                            <Field
                                                as={Input}
                                                type="number"
                                                id="simultaneousDeviceCount"
                                                name="simultaneousDeviceCount"
                                                min="1"
                                            />
                                            <ErrorMessage
                                                name="simultaneousDeviceCount"
                                                component="div"
                                                className="text-sm text-destructive"
                                            />
                                        </div>
                                    </CardContent>
                                </Card>
                            </div>
                        </div>

                        <div className="flex items-center justify-end gap-4 pt-6">
                            <Button
                                type="button"
                                variant="ghost"
                                onClick={() => navigate(-1)}
                            >
                                Cancel
                            </Button>
                            <Button
                                type="submit"
                                disabled={isSubmitting}
                            >
                                {isSubmitting && <Loader2 className="size-4 animate-spin" />}
                                {isSubmitting ? 'Creating...' : 'Create'}
                            </Button>
                        </div>
                    </FormikForm>
                )}
            </Formik>
        </PageShell>
    );
}
